import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { BedtimeSettings } from "../../shared/models";
import { isTyping, TYPING_HOLD_MS } from "../ui/keepTyping";
import { formatTime, useNow } from "../ui/time";
import { alarmRinging } from "../widgets/kitchen/alarm";
import { setDevicePref, SLEEP_DEFAULTS, useDevicePrefs, type SleepPrefs } from "./devicePrefs";
import { useHousehold } from "./useHousehold";
import { inBedtime, localMinutes } from "./screenDirector";
import { getDisplayInfo, setDisplay, type DisplayInfo } from "./wallDisplay";
import "./sleep.css";

/** Go to sleep now, whatever the time (the settings' Try it button). */
export const SLEEP_PREVIEW = "kinmantle:sleep-preview";
const ACTIVITY = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;
const CHECK_MS = 5_000;
// While the monitor is dimmed or off, say so again now and then: the helper turns
// it back on by itself if the page goes quiet (a crashed or reloading browser).
const RENEW_MS = 5 * 60_000;

/**
 * Bedtime on a wall display: once the household's bedtime hours start and nobody has
 * touched the screen for a few minutes, everything goes black but a dim clock that
 * moves every minute (nothing stays lit in one place all night), and on the wall
 * computer the monitor dims or goes to standby. A touch, the end of bedtime, a
 * severe-weather takeover or a ringing kitchen timer wakes it.
 */
export function useSleep(o: { enabled: boolean; hold: boolean; bedtime: BedtimeSettings; timeZone: string }) {
  const prefs = useDevicePrefs().sleep;
  const on = o.enabled && prefs.mode !== "none";
  const [asleep, setAsleep] = useState(false);
  const [bedtime, setBedtime] = useState(() => inBedtime(localMinutes(new Date(), o.timeZone), o.bedtime));
  const last = useRef(Date.now());
  const preview = useRef(false);
  const optsRef = useRef(o);
  optsRef.current = o;

  const wake = useCallback(() => {
    last.current = Date.now();
    preview.current = false;
    setAsleep(false);
  }, []);

  useEffect(() => {
    const touch = () => {
      last.current = Date.now();
    };
    for (const e of ACTIVITY) window.addEventListener(e, touch, { capture: true, passive: true });
    const start = () => {
      preview.current = true;
      setAsleep(true);
    };
    window.addEventListener(SLEEP_PREVIEW, start);
    return () => {
      for (const e of ACTIVITY) window.removeEventListener(e, touch, { capture: true });
      window.removeEventListener(SLEEP_PREVIEW, start);
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      const cur = optsRef.current;
      const now = inBedtime(localMinutes(new Date(), cur.timeZone), cur.bedtime);
      setBedtime(now);
      if (alarmRinging()) return wake();
      if (!now && !preview.current) return setAsleep(false);
      if (!on || cur.hold) return;
      const idle = Date.now() - last.current;
      if (now && idle >= prefs.idleMinutes * 60_000 && !(isTyping() && idle < TYPING_HOLD_MS)) setAsleep(true);
    };
    tick();
    const t = window.setInterval(tick, CHECK_MS);
    return () => clearInterval(t);
  }, [on, prefs.idleMinutes, wake]);

  // Something needs the screen: an alert takeover, the editor, or the setting turned off.
  useEffect(() => {
    if ((o.hold || (!on && !preview.current)) && asleep) wake();
  }, [o.hold, on, asleep, wake]);

  // The monitor follows: dimmed or off while asleep, back on when awake. A reload in
  // the middle of the night starts awake, so it also turns the monitor back on.
  const display = !asleep || prefs.mode === "clock" ? "on" : prefs.mode === "dim" ? "dim" : "off";
  useEffect(() => {
    setDisplay(display, prefs.dimLevel);
    if (display === "on") return;
    const t = window.setInterval(() => setDisplay(display, prefs.dimLevel), RENEW_MS);
    return () => clearInterval(t);
  }, [display, prefs.dimLevel]);

  return { asleep, wake, bedtime: on && bedtime };
}

/** All black but a dim clock that moves to a new spot every minute. */
export function SleepScreen({ onWake }: { onWake: () => void }) {
  const { timeZone, settings } = useHousehold();
  const now = useNow("minute");
  const minute = Math.floor(now.getTime() / 60_000);
  const spot = spotFor(minute);

  // The touch that wakes the screen doesn't reach what's underneath.
  const swallow = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onWake();
  };
  useEffect(() => {
    window.addEventListener("keydown", onWake);
    return () => window.removeEventListener("keydown", onWake);
  }, [onWake]);

  return createPortal(
    <div
      className="sleep"
      role="dialog"
      aria-label="Sleeping. Touch to wake."
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={swallow}
    >
      <div key={minute} className="sleep-clock" style={{ left: `${spot.x}%`, top: `${spot.y}%` }} aria-hidden="true">
        <strong>{formatTime(now, timeZone, settings.clock24h)}</strong>
        <span>{now.toLocaleDateString(undefined, { timeZone, weekday: "short", month: "short", day: "numeric" })}</span>
      </div>
    </div>,
    document.body,
  );
}

/** Where the clock sits in a given minute: anywhere over the middle of the screen. */
export function spotFor(minute: number): { x: number; y: number } {
  // A cheap, well-mixed hash of the minute, so it jumps around rather than sliding.
  let h = Math.imul(minute ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  const a = (h >>> 0) / 0x100000000;
  const b = (Math.imul(h, 0x27d4eb2d) >>> 0) / 0x100000000;
  return { x: Math.round(20 + a * 60), y: Math.round(22 + b * 56) };
}

const MODES: readonly (readonly [SleepPrefs["mode"], string])[] = [
  ["clock", "Dim clock"],
  ["dim", "Dim the backlight"],
  ["off", "Turn the screen off"],
  ["none", "Stay on the night screen"],
];

/** Settings → Display: bedtime and burn-in care for this screen. */
export function SleepSettings() {
  const device = useDevicePrefs();
  const prefs = device.sleep;
  const { settings } = useHousehold();
  const [info, setInfo] = useState<DisplayInfo | null | undefined>(device.wallComputer ? undefined : null);
  useEffect(() => {
    if (!device.wallComputer) return setInfo(null);
    let live = true;
    void getDisplayInfo().then((i) => live && setInfo(i));
    return () => {
      live = false;
    };
  }, [device.wallComputer]);
  const set = (patch: Partial<SleepPrefs>) => setDevicePref("sleep", { ...SLEEP_DEFAULTS, ...prefs, ...patch });
  const can = (mode: SleepPrefs["mode"]) => mode === "clock" || mode === "none" || (mode === "dim" ? !!info?.dim : !!info?.off);
  const range = `${formatTime(atClock(settings.bedtime.start), "UTC", settings.clock24h)}–${formatTime(atClock(settings.bedtime.end), "UTC", settings.clock24h)}`;

  return (
    <>
      <div className="field">
        <span>At bedtime ({range})</span>
        <div className="chip-row wrap">
          {MODES.map(([value, label]) => (
            <button key={value} className={`chip ${prefs.mode === value ? "on" : ""}`} disabled={!can(value) && prefs.mode !== value} onClick={() => set({ mode: value })}>
              {label}
            </button>
          ))}
        </div>
        <p className="muted small">
          {!device.autoScreens
            ? "Only screens in wall display mode go to sleep."
            : info === undefined
              ? "Checking what the wall computer can do with this monitor…"
              : info === null
                ? "Dimming the backlight and turning the screen off need the wall computer. On this screen the dim clock is the darkest it gets."
                : !info.dim && !info.off
                  ? "The wall computer can’t control this monitor, so the dim clock is the darkest it gets. Check that DDC/CI is on in the monitor’s own menu."
                  : `When nobody has touched the screen for a while, everything goes black but a dim clock that moves every minute. ${info.off ? "With the screen off, a touch turns it back on." : ""} The household’s bedtime hours are in Screens & schedule.`}
        </p>
      </div>
      {prefs.mode !== "none" && (
        <div className="ss-settings">
          <div className="field">
            <span>Goes to sleep after</span>
            <div className="chip-row wrap">
              {[1, 2, 5, 10, 15].map((m) => (
                <button key={m} className={`chip ${prefs.idleMinutes === m ? "on" : ""}`} onClick={() => set({ idleMinutes: m })}>
                  {m} min
                </button>
              ))}
            </div>
          </div>
          {prefs.mode === "dim" && (
            <div className="field">
              <span>Backlight while dimmed</span>
              <div className="chip-row wrap">
                {[0, 5, 10, 20, 30].map((l) => (
                  <button key={l} className={`chip ${prefs.dimLevel === l ? "on" : ""}`} onClick={() => set({ dimLevel: l })}>
                    {l}%
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <button className="btn" onClick={() => window.dispatchEvent(new Event(SLEEP_PREVIEW))}>
              Try it now
            </button>
          </div>
        </div>
      )}
      <label className="field field-toggle">
        <span>
          Shift the picture slightly
          <small className="muted">Every few minutes everything moves a pixel or two, too little to notice, so nothing stays lit in exactly one place for hours.</small>
        </span>
        <input type="checkbox" className="switch" checked={device.pixelShift} onChange={(e) => setDevicePref("pixelShift", e.target.checked)} />
      </label>
    </>
  );
}

const atClock = (hhmm: string) => new Date(`2000-01-01T${hhmm}:00Z`);
